import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { typography } from '../../styles/typography';

const STORAGE_KEY = 'platform_order_settings';

const PLATFORMS = [
  { id: 'swiggy', label: 'Swiggy', description: 'Accept orders from Swiggy' },
  { id: 'zomato', label: 'Zomato', description: 'Accept orders from Zomato' },
  { id: 'magic_pin', label: 'Magic Pin', description: 'Accept orders from Magic Pin' },
];

const defaultPlatformSettings = () =>
  PLATFORMS.reduce((acc, p) => ({ ...acc, [p.id]: true }), {});

const loadSavedSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          ...defaultPlatformSettings(),
          ...parsed,
          restaurantOpen: parsed.restaurantOpen !== false,
        };
      }
    }
  } catch (_) {
    // ignore
  }
  return { restaurantOpen: true, ...defaultPlatformSettings() };
};

const saveSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (_) {
    // ignore
  }
};

const Toggle = ({ checked, onChange, disabled, 'aria-label': ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`
      relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-0
      transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2
      ${checked ? 'bg-green-500' : 'bg-gray-300'}
      ${disabled ? 'cursor-not-allowed opacity-60' : ''}
    `}
  >
    <span
      className={`
        absolute top-1/2 left-1 h-5 w-5 -translate-y-1/2 rounded-full bg-white
        shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out
        ${checked ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

const PlatformSettingScreen = () => {
  const [settings, setSettings] = useState(loadSavedSettings);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const handleRestaurantOpenToggle = useCallback(() => {
    setSettings((prev) => {
      const nextOpen = !prev.restaurantOpen;
      const next = { ...prev, restaurantOpen: nextOpen };
      PLATFORMS.forEach((p) => {
        next[p.id] = nextOpen;
      });
      return next;
    });
  }, []);

  const handleToggle = useCallback((platformId) => {
    setSettings((prev) => ({
      ...prev,
      [platformId]: !prev[platformId],
    }));
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, delay: i * 0.05 },
    }),
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          initial="hidden"
          animate="visible"
          className="space-y-8"
          variants={{
            visible: { transition: { staggerChildren: 0.05 } },
          }}
        >
          {/* Header */}
          <div className="mb-6">
            <h1 className={`${typography.h3} text-gray-800`}>Platform Settings</h1>
            <p className={`${typography.p} mt-1 text-gray-600`}>
              Choose which platforms can send orders to your outlet. Turn off a platform to stop receiving orders from it.
            </p>
          </div>

          {/* Restaurant Open / Close + Platform list */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Restaurant Open / Close - above all platforms */}
            <motion.div
              variants={cardVariants}
              custom={0}
              className="flex items-center justify-between gap-4 px-5 py-4 bg-gray-50/80 border-b border-gray-100"
            >
              <div className="min-w-0 flex-1">
                <p className={`${typography.sh1} font-semibold text-gray-800`}>
                  Restaurant
                </p>
                <p className={`${typography.p} text-sm text-gray-500 mt-0.5`}>
                  {settings.restaurantOpen
                    ? 'Restaurant is open — accepting orders'
                    : 'Restaurant is closed — not accepting orders'}
                </p>
              </div>
              <div className="flex items-center shrink-0 gap-3">
                <span
                  className={`text-sm font-medium ${settings.restaurantOpen ? 'text-green-600' : 'text-gray-500'}`}
                >
                  {settings.restaurantOpen ? 'Open' : 'Closed'}
                </span>
                <Toggle
                  checked={!!settings.restaurantOpen}
                  onChange={handleRestaurantOpenToggle}
                  aria-label={settings.restaurantOpen ? 'Close restaurant' : 'Open restaurant'}
                />
              </div>
            </motion.div>
            <ul className="divide-y divide-gray-100">
              {PLATFORMS.map((platform, index) => (
                <motion.li
                  key={platform.id}
                  variants={cardVariants}
                  custom={index}
                  className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className={`${typography.sh1} font-semibold text-gray-800`}>
                      {platform.label}
                    </p>
                    <p className={`${typography.p} text-sm text-gray-500 mt-0.5`}>
                      {platform.description}
                    </p>
                  </div>
                  <div className="flex items-center shrink-0 gap-3">
                    <span
                      className={`text-sm font-medium ${settings[platform.id] ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {settings[platform.id] ? 'Orders on' : 'Orders off'}
                    </span>
                    <Toggle
                      checked={!!settings[platform.id]}
                      onChange={() => handleToggle(platform.id)}
                      aria-label={`${settings[platform.id] ? 'Disable' : 'Enable'} orders from ${platform.label}`}
                    />
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Hint */}
          <p className={`${typography.p} text-sm text-gray-500`}>
            Changes are saved automatically. When a platform is turned off, new orders from that platform will not be accepted.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PlatformSettingScreen;
